import { OnSubscriptionDataOptions, useSubscription } from 'react-apollo';
import gql from 'graphql-tag';

import { getDeviceInfo } from '../lib/device-info';
import { SubResponse } from '../apollo/types';
import { withSubDedup } from '../apollo/with-sub-dedup';
import { CardReadMessage } from './card-message';

export default function usePeripheralMessagesSubscription({
  onCardNotFound,
  onInvalidPlayerCardRead,
  onPlayerCardRead
}: {
  onCardNotFound(data: InvalidCardMessage): void;
  onInvalidPlayerCardRead(data: InvalidCardMessage): void;
  onPlayerCardRead(data: CardReadMessage): void;
}) {
  const { slotId } = getDeviceInfo();

  const handlersByType: Record<MessageType, (data: InvalidCardMessage | CardReadMessage) => void> = {
    CARD_NOT_FOUND: data => onCardNotFound(data as InvalidCardMessage),
    INVALID_PLAYER_CARD_READ: data => onInvalidPlayerCardRead(data as InvalidCardMessage),
    PLAYER_CARD_READ: data => onPlayerCardRead(data as CardReadMessage)
  };

  useSubscription<SubResponse<Data>, Variables>(graphql, {
    onSubscriptionData: withSubDedup(onSubscriptionData),
    variables: {
      deviceType: slotId.type,
      messageTypes,
      slotNumber: slotId.slotNumber,
      venueId: slotId.venueId
    }
  });

  async function onSubscriptionData({
    subscriptionData: { data }
  }: OnSubscriptionDataOptions<SubResponse<Data, MessageType>>) {
    if (data) {
      const { metadata, payload } = data.sub;

      handlersByType[metadata.message.type](payload);
    }
  }
}

type MessageType = (typeof messageTypes)[number];

const messageTypes = ['CARD_NOT_FOUND', 'INVALID_PLAYER_CARD_READ', 'PLAYER_CARD_READ'] as const;

type Variables = {
  deviceType: string;
  messageTypes: readonly string[];
  slotNumber: number;
  venueId: string;
};

type Data = InvalidCardMessage | CardReadMessage;

export type InvalidCardMessage = {
  errorCode: string;
};

const graphql = gql`
  subscription ($venueId: String!, $deviceType: String!, $slotNumber: Int!, $messageTypes: [String!]!) {
    sub: peripheralMessages(
      where: {
        schemaVersion: "1.0.0"
        venueId: $venueId
        deviceType: $deviceType
        slotNumber: $slotNumber
        messageTypes: $messageTypes
      }
    ) {
      metadata {
        request {
          id
          venueId
        }
        message {
          type
        }
      }
      payload {
        timestamp
        ... on InvalidCardReadMessagePayload {
          errorCode
          errorMessage
        }
        ... on InvalidDataMessagePayload {
          errorCode
          errorMessage
        }
        ... on PlayerCardMessagePayload {
          cardNumber
          cardOperator
          cardStatus
          player {
            banned
            barred
            closed
            external
            selfExcluded
            primaryIdentifier
            firstname
            surname
            rewardPoints
          }
          trackData {
            track1
            track2
            track3
          }
        }
      }
    }
  }
`;
